/**
 * legUtils.ts
 * 
 * Utility functions for journey leg operations.
 * Handles leg lookups, transport mode detection, and outdated value checks.
 * 
 * A "leg" represents the transport connection between two consecutive stops.
 * In v2 format, legs are stored at the root level with references to fromStopId and toStopId.
 */

import type { Change } from '../../../contexts/JourneyEditContext';

/**
 * Gets the leg that leads TO a stop (incoming transport).
 * Supports both v2 format (separate legs array) and v1 format (transport data on stops).
 * 
 * @param journey - The journey object
 * @param stopId - The ID of the destination stop
 * @returns The leg object or null if not found
 */
export function getLegToStop(journey: any, stopId: string): any | null {
  if (!stopId) return null;
  
  // Try v2 format first (separate legs array)
  if (journey?.legs && journey.legs.length > 0) {
    const leg = journey.legs.find((leg: any) => leg.toStopId === stopId);
    if (leg) return leg;
  }
  
  // Fall back to v1 format (transport data on stop itself)
  // In v1, transport data is stored on the destination stop
  const stops = journey?.stops || [];
  const stopIndex = stops.findIndex((s: any) => s.id === stopId);
  if (stopIndex <= 0) return null; // First stop has no incoming leg
  
  const stop = stops[stopIndex];
  const prevStop = stops[stopIndex - 1];
  
  // Create a virtual leg object from v1 stop data
  return {
    id: `virtual-leg-${prevStop?.id}-${stop?.id}`,
    fromStopId: prevStop?.id,
    toStopId: stop?.id,
    transportMode: stop.transport_mode_from_previous || 'auto',
    distanceKm: stop.distance_from_previous_km || 0,
    durationHours: stop.transport_time_from_previous_hours || 0,
    costCents: (stop.transport_cost_eur || 0) * 100,
    co2eKg: stop.transport_co2e_kg_from_previous || 0,
    hasOutdatedValues: stop.has_outdated_transport || false,
  };
}

/**
 * Gets the leg that departs FROM a stop (outgoing transport).
 * Supports both v2 format (separate legs array) and v1 format (transport data on stops).
 * 
 * @param journey - The journey object
 * @param stopId - The ID of the origin stop
 * @returns The leg object or null if not found
 */
export function getLegFromStop(journey: any, stopId: string): any | null {
  if (!stopId) return null;
  
  // Try v2 format first (separate legs array)
  if (journey?.legs && journey.legs.length > 0) {
    const leg = journey.legs.find((leg: any) => leg.fromStopId === stopId);
    if (leg) return leg;
  }
  
  // Fall back to v1 format (transport data on next stop)
  const stops = journey?.stops || [];
  const stopIndex = stops.findIndex((s: any) => s.id === stopId);
  if (stopIndex < 0 || stopIndex >= stops.length - 1) return null; // Last stop has no outgoing leg
  
  const stop = stops[stopIndex];
  const nextStop = stops[stopIndex + 1];
  
  // Create a virtual leg object from v1 stop data
  return {
    id: `virtual-leg-${stop?.id}-${nextStop?.id}`,
    fromStopId: stop?.id,
    toStopId: nextStop?.id,
    transportMode: nextStop.transport_mode_from_previous || 'auto',
    distanceKm: nextStop.distance_from_previous_km || 0,
    durationHours: nextStop.transport_time_from_previous_hours || 0,
    costCents: (nextStop.transport_cost_eur || 0) * 100,
    co2eKg: nextStop.transport_co2e_kg_from_previous || 0,
    hasOutdatedValues: nextStop.has_outdated_transport || false,
  };
}

/**
 * Gets transport data for a stop from its incoming leg.
 * Returns normalized transport object with default values.
 * Supports both v1 and v2 journey formats.
 * 
 * @param journey - The journey object
 * @param stopId - The ID of the stop
 * @returns Transport data object with mode, distance, duration, cost, co2
 */
export function getTransportToStop(journey: any, stopId: string) {
  const leg = getLegToStop(journey, stopId);
  if (!leg) {
    return { mode: '', distance: 0, duration: 0, cost: 0, co2: 0, hasOutdatedValues: false };
  }
  return {
    mode: leg.transportMode || 'auto',
    distance: Number(leg.distanceKm) || 0,
    duration: Number(leg.durationHours) || 0,
    cost: (Number(leg.costCents) || 0) / 100,
    co2: Number(leg.co2eKg) || 0,
    hasOutdatedValues: leg.hasOutdatedValues || false
  };
}

/**
 * Gets the effective transport mode for a stop, considering pending changes.
 * Uses the LAST (most recent) pending change if multiple changes exist for the same leg.
 * 
 * @param journey - The journey object
 * @param stopId - The ID of the stop
 * @param pendingChanges - Array of pending changes
 * @returns The effective transport mode string
 */
export function getEffectiveTransportMode(
  journey: any,
  stopId: string,
  pendingChanges: Change[]
): string {
  const leg = getLegToStop(journey, stopId);
  if (!leg) return '';
  
  // Check for pending transport change - get the LAST (most recent) one
  const matchingChanges = pendingChanges.filter((change: any) =>
    change.type === 'update_leg_transport' && change.legId === leg.id
  );
  const pendingChange = matchingChanges.length > 0 
    ? matchingChanges[matchingChanges.length - 1] as any 
    : null;
  
  if (pendingChange && pendingChange.type === 'update_leg_transport') {
    return pendingChange.transportMode;
  }
  
  return leg.transportMode || '';
}

/**
 * Checks if a leg (identified by the TO stop) has outdated values.
 * Supports both v1 and v2 journey formats.
 * 
 * A leg is considered outdated if:
 * 1. Its transport mode was explicitly changed (pending change exists)
 * 2. The FROM stop (origin) was replaced
 * 3. The TO stop (destination) was replaced
 * 4. This leg involves a newly added stop (from add_stop)
 * 5. This leg has zero values (indicates it needs recalculation)
 * 6. The leg has the hasOutdatedValues flag from backend or v1 has_outdated_transport
 * 
 * @param journey - The journey object
 * @param stopId - The ID of the TO stop (destination of the leg)
 * @param pendingChanges - Array of pending changes
 * @returns True if the leg has outdated values
 */
export function isLegOutdated(
  journey: any,
  stopId: string,
  pendingChanges: Change[]
): boolean {
  const leg = getLegToStop(journey, stopId);
  if (!leg) return false;
  
  const stops = journey.stops || [];
  const toStop = stops.find((s: any) => s.id === stopId);
  const fromStop = stops.find((s: any) => s.id === leg.fromStopId);
  
  if (!toStop) return false;
  
  // 1. Check if there's a pending transport change for this leg
  // For virtual legs (v1), we check by legId OR by matching the leg's from/to stop IDs
  const hasPendingTransportChange = pendingChanges.some((change: any) => {
    if (change.type !== 'update_leg_transport') return false;
    
    // Check by legId (for v2 legs with real IDs)
    if (change.legId === leg.id) return true;
    
    // For v1 virtual legs, check by targetStopOrder (the TO stop order)
    if (change.targetStopOrder === toStop.order) return true;
    
    return false;
  });
  
  // 2. Check if the TO stop (this stop) was replaced
  const toStopWasReplaced = pendingChanges.some((change: any) =>
    change.type === 'replace_stop' && change.stopId === stopId
  );
  
  // 3. Check if the FROM stop was replaced
  // Note: We check both by ID and by position to handle v2 legs with potentially stale data
  const fromStopWasReplaced = pendingChanges.some((change: any) => {
    if (change.type !== 'replace_stop') return false;
    
    // Check by ID
    if (fromStop && change.stopId === fromStop.id) {
      return true;
    }
    
    // Check by position (for v2 legs where the fromStop ID might be stale)
    // If a stop at order N was replaced, the leg FROM that stop (arriving at order N+1) is affected
    if (fromStop?.order && change.stopOrder === fromStop.order) {
      return true;
    }
    
    // Also check if the replaced stop is immediately before this leg's destination
    // This catches cases where we're checking the outgoing leg from the replaced stop
    if (toStop?.order && toStop.order > 1) {
      const stops = journey.stops || [];
      const prevStopIndex = stops.findIndex((s: any) => s.order === toStop.order - 1);
      if (prevStopIndex >= 0) {
        const prevStop = stops[prevStopIndex];
        if (prevStop && change.stopId === prevStop.id) {
          return true;
        }
      }
    }
    
    return false;
  });
  
  // 4. Check if this leg involves a newly added stop
  // When a stop is added at position X, it creates two new/modified legs:
  // - The leg TO position X (arriving at the new stop)
  // - The leg FROM position X to X+1 (departing from the new stop to what was the next stop)
  // Both of these legs should be marked as outdated.
  const hasAffectingAddStop = pendingChanges.some((change: any) => {
    if (change.type !== 'add_stop') return false;
    
    const newStopId = (change as any).stopId;
    const addPosition = (change as any).position;
    
    // Method 1: Check by ID (works for v1 virtual legs where IDs are correctly updated)
    if (newStopId && (leg.fromStopId === newStopId || leg.toStopId === newStopId)) {
      return true;
    }
    
    // Method 2: Check by position (works for v2 legs that may have stale fromStopId)
    // When a stop is added at position X:
    // - Leg TO position X (the leg arriving at the new stop) is affected
    // - Leg TO position X+1 (the leg departing from the new stop) is affected
    // We check if this leg's destination (toStop) is at either of these positions
    if (addPosition && toStop?.order) {
      if (toStop.order === addPosition || toStop.order === addPosition + 1) {
        return true;
      }
    }
    
    return false;
  });
  
  // 5. Check if this leg is affected by a stop removal
  // When a stop at position X is removed:
  // - The leg TO position X and the leg FROM position X are merged into one
  // - The merged leg ends at what was position X+1, now position X after reordering
  // So the leg that NOW ends at position X (where X was the removed stop's order) is affected
  const hasAffectingRemoveStop = pendingChanges.some((change: any) => {
    if (change.type !== 'remove_stop') return false;
    
    // Get the removed stop's original order
    const removedOrder = (change as any).stopOrder;
    
    if (removedOrder && toStop?.order) {
      // After removal, the stop that was at order removedOrder+1 is now at removedOrder
      // So if this leg ends at the position that equals the removed stop's original order,
      // it's the merged leg and should be marked as outdated
      if (toStop.order === removedOrder) {
        return true;
      }
    }
    
    // Also check if the removed stop was the FROM or TO of this leg (by ID)
    // This catches cases where the v1 data still references the removed stop
    const removedStopId = (change as any).stopId;
    if (removedStopId) {
      // The removed stop can't be fromStop or toStop anymore (it's removed),
      // but if the original leg data still has references to it, mark as outdated
      if (leg.fromStopId === removedStopId || leg.toStopId === removedStopId) {
        return true;
      }
    }
    
    return false;
  });
  
  // 6. Check if this leg has zero values (needs recalculation)
  // This is a fallback check for cases where v1 data was properly zeroed
  const hasZeroValues = (
    leg.distanceKm === 0 &&
    leg.co2eKg === 0 &&
    (leg.costCents === 0 || leg.costCents === undefined) &&
    leg.durationHours === 0 &&
    leg.transportMode // Has a transport mode but zero values
  );
  
  // A leg with zero values should be considered outdated if there are ANY pending changes
  const hasAnyPendingChange = pendingChanges.length > 0 && hasZeroValues;
  
  return (
    leg.hasOutdatedValues || 
    hasPendingTransportChange || 
    toStopWasReplaced || 
    fromStopWasReplaced || 
    hasAffectingAddStop ||
    hasAffectingRemoveStop ||
    hasAnyPendingChange
  );
}

/**
 * Checks if ANY leg in the journey has outdated values.
 * Used for showing the asterisk on journey totals.
 * 
 * @param journey - The journey object
 * @param pendingChanges - Array of pending changes
 * @returns True if any leg has outdated values
 */
export function hasAnyOutdatedLeg(
  journey: any,
  pendingChanges: Change[]
): boolean {
  if (!journey?.stops) return false;
  
  // Check each stop (except the first one, which has no incoming leg)
  return journey.stops.some((stop: any, index: number) => {
    if (index === 0) return false;
    return isLegOutdated(journey, stop.id, pendingChanges);
  });
}

